import { type Creator, type CreatorsV2Filter } from '@metapic-apps/shared'
import { computed, type Ref, ref } from 'vue'

import { buildUsersIndexParams, normalizeCreator } from '@/lib/adapters/creators.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { metapicApiClient } from '@/lib/services/metapic-api.service'
import { useErrorStore } from '@/stores/error'

type CreatorStatusActionType = 'approve' | 'ban'

export const useGetCreators = (filterRef: Ref<CreatorsV2Filter>) => {
  const {
    data: response,
    isLoading,
    error,
    fetchData: refetch,
  } = useApi((signal) =>
    metapicApiClient.usersIndex(buildUsersIndexParams(filterRef.value), { signal }),
  )

  const data = computed<Creator[]>(() => (response.value?.data ?? []).map(normalizeCreator))
  const totalPages = computed(() => response.value?.meta.lastPage ?? 0)

  return { data, totalPages, isLoading, error, refetch }
}

export const useChangeCreatorsStatus = () => {
  const isLoading = ref(false)
  const errorStore = useErrorStore()

  const changeCreatorsStatus = async (
    userIds: number[],
    action: CreatorStatusActionType,
  ): Promise<number | null> => {
    try {
      isLoading.value = true
      const endpoint = action === 'approve' ? '/v2/approve-users' : '/v2/ban-users'
      const response = await request.post<{ approved?: number; banned?: number }>(endpoint, {
        user_ids: userIds,
      })
      const changedAmount = action === 'approve' ? response.data.approved : response.data.banned
      return changedAmount ?? null
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { changeCreatorsStatus, isLoading }
}
